import React, { useState, useEffect } from 'react';
import {
  Container,
  Box,
  Button,
  Typography,
  Alert,
  Grid,
} from '@mui/material';
import { getAccessToken } from '../../utils/auth';
import DateSelector from './DateSelector.js';
import EditEndTimeDialog from './EditEndTimeDialog.js';
import CheckboxGroup from './CheckboxGroup.js';
import TimeInputFields from './TimeInputFields.js';
import OpenRegistrationsList from './OpenRegistrationsList.js';
import RegistrationsList from './RegistrationsList.js';

function TimeRegistration() {
  const [date, setDate] = useState(new Date());
  const [startTime, setStartTime] = useState('');
  const [isSick, setIsSick] = useState(false);
  const [isVacation, setIsVacation] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [openRegistrations, setOpenRegistrations] = useState([]);
  const [registrations, setRegistrations] = useState([]);
  const [selectedRegistration, setSelectedRegistration] = useState(null);
  const [newEndTime, setNewEndTime] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  
  const handleEndTime = (registration) => {
    setSelectedRegistration(registration);
    setOpenDialog(true);
  };
  
  const handleUpdateRegistration = async () => {
    if (!newEndTime) {
      setError('Venligst angiv afgangstid.');
      return;
    }

    const data = {
      id: selectedRegistration.id,
      endTime: newEndTime,
    };

    try {
      const token = await getAccessToken();
      const response = await fetch('https://app.recyconelement.dk/api/time/update_time_registration.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setMessage('Tidsregistrering opdateret med succes!');
        setError(null);
        setOpenDialog(false);
        setNewEndTime('');
        fetchOpenRegistrations(); // Opdater listen
        fetchRegistrations(); // Opdater listen
      } else {
        setError('Fejl: ' + result.message);
      }
    } catch (error) {
      console.error('Fejl ved opdatering af tidsregistrering:', error);
      setError('Der opstod en fejl. Prøv igen senere.');
    }
  };

  
  useEffect(() => {
    fetchOpenRegistrations();
    fetchRegistrations();
  }, []);  
  
  const fetchRegistrations = async () => {
    try {
      const token = await getAccessToken();
      const response = await fetch('https://app.recyconelement.dk/api/time/get_time_registrations.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setRegistrations(result.data);
      } else {
        setError('Fejl: ' + result.message);
      }
    } catch (error) {
      console.error('Fejl ved hentning af åbne tidsregistreringer:', error);
      setError('Der opstod en fejl. Prøv igen senere.');
    }
  };
  
  const fetchOpenRegistrations = async () => {
    try {
      const token = await getAccessToken();
      
      const response = await fetch('https://app.recyconelement.dk/api/time/get_open_time_registrations.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setOpenRegistrations(result.data);
      } else {
        setError('Fejl: ' + result.message);
      }
    } catch (error) {
      console.error('Fejl ved hentning af åbne tidsregistreringer:', error);
      setError('Der opstod en fejl. Prøv igen senere.');
      setMessage(null);
    }
  };
  
  const handleSubmit = async(e) => {
    e.preventDefault();
    console.log(startTime);
    if (!startTime && !isSick && !isVacation) {
      setError('Venligst angiv ankomsttid eller marker som syg/ferie.');
      setMessage(null);
      return;
    } 

    // Forbered data til backend
    const type = isSick ? '51' : isVacation ? '60' : '01';
    const breakTime = date.getDay() === 5 ? 0 : 3000; // 0 minutter hvis fredag, ellers 30 minutter
    const data = {
      date: date.toISOString().split('T')[0],
      startTime: startTime || null,
      endTime: null,
      isSick: isSick,
      isVacation: isVacation,
      type: type,
      break: breakTime,
    };

    try {
      const token = await getAccessToken();
      const response = await fetch('https://app.recyconelement.dk/api/time/time_registration.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer ' + token,
        },
        body: JSON.stringify(data),
      });

      const result = await response.json();
      
      console.log(response);

      if (response.ok && result.success) {
        setMessage('Tidsregistrering opdateret med succes!');
        setError(null);
        setOpenDialog(false);
        setNewEndTime('');
        fetchOpenRegistrations(); // Opdater listen med åbne tidsregistreringer
		fetchRegistrations(); // Opdater listen
		
      } else {
        setError('Fejl: ' + result.message);
        setMessage(null);
      }
    } catch (error) {
      console.error('Fejl ved opdatering af tidsregistrering:', error);
      setError('Der opstod en fejl. Prøv igen senere.');
      setMessage(null);
    }
  };

  // Deaktiver tidspunktsfelter, hvis syg eller ferie er markeret
  const disableTimeFields = isSick || isVacation;

  return (
  <>
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          marginBottom: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography component="h1" variant="h5">
          Tidregistrering
        </Typography>
        {error && (
          <Alert severity="error" sx={{ mt: 2, width: '100%' }}>
            {error}
          </Alert>
        )}
        {message && (
          <Alert severity="success" sx={{ mt: 2, width: '100%' }}>
            {message}
          </Alert>
        )}
		{openRegistrations.length > 0 && (
		  <OpenRegistrationsList 
			openRegistrations={openRegistrations}
			handleEndTime={handleEndTime}
		  />
		)}
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            {/* Dato */}
            <DateSelector date={date} setDate={setDate} />

            {/* Sygedag */}
			<CheckboxGroup 
			  isSick={isSick}
			  setIsSick={setIsSick}
			  isVacation={isVacation}
			  setIsVacation={setIsVacation}
			/>

            {/* Kommetid */}
            <TimeInputFields
			  startTime={startTime}
			  setStartTime={setStartTime}
			  disableTimeFields={disableTimeFields}
			/>

            {/* Submit-knap */}
            <Grid item xs={12}>
              <Button
                type="submit"
                fullWidth
                variant="contained"
                sx={{ mt: 1 }}
              >
                Registrer
              </Button>
            </Grid>
          </Grid>
        </Box>

{registrations.length > 0 && (
  <RegistrationsList registrations={registrations} />
)}

      </Box>
    </Container>
	<EditEndTimeDialog
	  open={openDialog}
	  onClose={() => setOpenDialog(false)}
	  onSave={handleUpdateRegistration}
	  newEndTime={newEndTime}
	  setNewEndTime={setNewEndTime}
	/>
  </>
  );
}

export default TimeRegistration;
